// Bots that drive a game through its input for minutes of sim time while the invariant watch looks on (engine
// suggestions: seeded monkey, route and play bots). Both are pure logic: the game reads their output each sim step
// and turns it into input; they never touch a device, a clock or Math.random, so a run replays from its seed.
//
// - The route bot walks a list of legs (a target point and a timeout each) and records, per leg, whether it got
//   there, how long it took and the highest point it reached; a leg can expect to be blocked (a step too high), which
//   then passes when the target is NOT reached.
// - The monkey picks a random input from weighted choices and holds it for a random time, over and over.

/** A seeded random source in [0, 1) (mulberry32): small, fast, the same on every engine. Print the seed with a run. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A point; y is optional for a target (only x and z are steered to). */
export interface BotPoint {
  x: number;
  y?: number;
  z: number;
}

/** One leg of a route. Extra fields (how to move on it: sprint, jump) are the game's and come back with the leg. */
export interface RouteLeg {
  name: string;
  to: BotPoint;
  /** Give up after this long (s). */
  timeout: number;
  /** "reach" (default): the target must be reached; "blocked": it must not be (a step too high, a slope too steep). */
  expect?: "reach" | "blocked";
  /** Reached within this distance in x and z (m). Default 0.5. */
  radius?: number;
  /** For a "reach" leg, also needs this height (m) at the target: on top of the step, not beside it. */
  minY?: number;
}

/** How a leg went. */
export interface LegResult {
  name: string;
  expect: "reach" | "blocked";
  reached: boolean;
  /** Whether the leg did what it expected. */
  ok: boolean;
  /** Seconds spent on it. */
  time: number;
  /** The highest y and the closest x/z distance to the target seen during it. */
  maxY: number;
  closest: number;
}

export interface RouteBot<L extends RouteLeg> {
  /** The leg being walked and the unit direction to steer this step (world x, z), or null when the route is done. */
  steer(): { leg: L; dir: { x: number; z: number }; distance: number } | null;
  /** Call once after each sim step with the time step. */
  update(dt: number): void;
  readonly results: readonly LegResult[];
  readonly done: boolean;
}

/** A route bot over `legs`, reading the driven thing's position from `pos`. */
export function createRouteBot<L extends RouteLeg>(legs: readonly L[], pos: () => { x: number; y: number; z: number }): RouteBot<L> {
  const results: LegResult[] = [];
  let i = 0;
  let time = 0;
  let maxY = -Infinity;
  let closest = Infinity;
  const flat = (l: L): number => {
    const p = pos();
    return Math.hypot(l.to.x - p.x, l.to.z - p.z);
  };
  const reachedNow = (l: L): boolean => flat(l) <= (l.radius ?? 0.5) && (l.minY === undefined || pos().y >= l.minY);
  const finish = (l: L, reached: boolean): void => {
    const expect = l.expect ?? "reach";
    results.push({ name: l.name, expect, reached, ok: expect === "reach" ? reached : !reached, time, maxY, closest });
    i++;
    time = 0;
    maxY = -Infinity;
    closest = Infinity;
  };
  return {
    steer() {
      const l = legs[i];
      if (!l) return null;
      const p = pos();
      const dx = l.to.x - p.x;
      const dz = l.to.z - p.z;
      const d = Math.hypot(dx, dz);
      return { leg: l, dir: d > 1e-6 ? { x: dx / d, z: dz / d } : { x: 0, z: 0 }, distance: d };
    },
    update(dt) {
      const l = legs[i];
      if (!l) return;
      time += dt;
      maxY = Math.max(maxY, pos().y);
      closest = Math.min(closest, flat(l));
      if (reachedNow(l)) finish(l, true);
      else if (time >= l.timeout) finish(l, false);
    },
    get results() {
      return results;
    },
    get done() {
      return i >= legs.length;
    },
  };
}

/** One kind of input the monkey may pick. */
export interface MonkeyChoice<T> {
  /** Relative weight. */
  weight: number;
  /** Build one input of this kind from the random source. */
  make(random: () => number): T;
}

export interface Monkey<T> {
  readonly seed: number;
  /** The input for this step (advances the hold by `dt`). */
  next(dt: number): T;
  /** Inputs picked so far. */
  readonly bursts: number;
}

/** A seeded monkey: picks an input from `choices` by weight, holds it `minHold`..`maxHold` s, then picks again. */
export function createMonkey<T>(seed: number, choices: readonly MonkeyChoice<T>[], o: { minHold: number; maxHold: number }): Monkey<T> {
  if (!choices.length) throw new Error("monkey: no choices");
  const random = seededRandom(seed);
  const total = choices.reduce((a, c) => a + c.weight, 0);
  let left = 0;
  let current: T | null = null;
  let bursts = 0;
  const pick = (): T => {
    let r = random() * total;
    for (const c of choices) if ((r -= c.weight) < 0) return c.make(random);
    return choices[choices.length - 1]!.make(random);
  };
  return {
    seed,
    next(dt) {
      if (left <= 0 || current === null) {
        current = pick();
        bursts++;
        left = o.minHold + random() * (o.maxHold - o.minHold);
      }
      left -= dt;
      return current;
    },
    get bursts() {
      return bursts;
    },
  };
}
